/**
 * Part-to-whole bar for a set of attendance counts.
 *
 * Segments are separated by a two-pixel surface gap so adjacent status colours never touch,
 * and the whole bar carries a text summary for assistive technology. Used as the session
 * meter on the roll call and as the per-person distribution in the roster.
 */

import type { ReactNode } from 'react';

import { countOf, type StateCounts } from '../../../shared/stats.js';
import { ALL_APPEARANCES, type StateAppearance } from '../attendance.js';

/** One drawn segment: an appearance and how many slots it covers. */
interface Segment {
  appearance: StateAppearance;
  count: number;
}

/** Orders the counts into drawing order, dropping states with no slots. */
function segmentsOf(counts: StateCounts): Segment[] {
  return ALL_APPEARANCES
    .map((appearance) => ({ appearance, count: countOf(counts, appearance.state) }))
    .filter((segment) => segment.count > 0);
}

/** Builds the sentence read out in place of the bar. */
function summaryOf(segments: readonly Segment[], total: number): string {
  if (total === 0) return 'Nothing recorded yet.';
  return segments.map((segment) => `${segment.count} ${segment.appearance.label.toLowerCase()}`).join(', ');
}

/** A horizontal stacked bar showing how a group of slots divides between the five states. */
export function StatusBar({
  counts,
  height = 10,
}: {
  counts: StateCounts;
  /** Bar thickness in pixels. */
  height?: number;
}): ReactNode {
  const segments = segmentsOf(counts);
  const total = segments.reduce((sum, segment) => sum + segment.count, 0);

  if (total === 0) {
    return (
      <div
        className="w-full rounded-full bg-sunken"
        style={{ height }}
        role="img"
        aria-label="Nothing recorded yet."
      />
    );
  }

  return (
    <div
      className="flex w-full overflow-hidden rounded-full"
      style={{ height, gap: segments.length > 1 ? 2 : 0 }}
      role="img"
      aria-label={summaryOf(segments, total)}
    >
      {segments.map((segment) => (
        <div
          key={segment.appearance.state}
          // A segment too narrow to read still answers what it is when pointed at.
          title={`${segment.count} ${segment.appearance.label.toLowerCase()}`
            + ` · ${Math.round((segment.count / total) * 100)}%`}
          className="h-full rounded-full"
          style={{
            width: `${(segment.count / total) * 100}%`,
            backgroundColor: segment.appearance.cssVar,
          }}
        />
      ))}
    </div>
  );
}
